import { useState } from "react";
import { Proposals, type Proposal } from "../../../lib/apiTyped";
import { useResource } from "../../../lib/useResource";
import { useApiCall } from "../../../lib/useToastError";
import { useNotifications } from "../../../components/shared/NotificationsProvider";
import { PageHeader } from "../../../components/AppLayout";
import { DataTable, type Column } from "../../../components/shared/DataTable";
import { StatusBadge } from "../../../components/shared/StatusBadge";
import { Button } from "../../../components/shared/Button";
import { Modal } from "../../../components/shared/Modal";
import { FormInput } from "../../../components/shared/FormInput";
import { fmtEur, fmtDate } from "../../../lib/format";

export function AdminProposals() {
  const { data, loading, refetch } = useResource(() => Proposals.list(), []);
  const { run } = useApiCall();
  const { confirm, toast } = useNotifications();
  const [createOpen, setCreateOpen] = useState(false);

  async function cancel(id: string) {
    const ok = await confirm({
      title: "Cancel this proposal?",
      message: "The Stripe checkout link will be invalidated immediately.",
      confirmLabel: "Cancel proposal",
      variant: "danger",
    });
    if (!ok) return;
    await run(() => Proposals.cancel(id), { successMessage: "Cancelled." });
    await refetch();
  }
  function copyLink(link: string) {
    navigator.clipboard.writeText(link).then(() => toast("Link copied", "success"));
  }

  const columns: Column<Proposal>[] = [
    { header: "Org", render: (r) => <code className="text-xs">{r.orgId}</code> },
    { header: "Concept", render: (r) => r.concept },
    { header: "Amount", align: "right", render: (r) => fmtEur(r.amountEur) },
    { header: "IVA", align: "right", render: (r) => `${r.ivaRate}%` },
    { header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { header: "Created", render: (r) => fmtDate(r.createdAt) },
  ];

  return (
    <>
      <PageHeader
        title="Proposals"
        subtitle="One-time billing for custom development, custom skills, etc. Each proposal is a Stripe Checkout link the client can pay; an invoice is auto-emitted on success."
        action={
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            + New proposal
          </Button>
        }
      />

      <DataTable
        rows={data?.proposals ?? null}
        loading={loading}
        rowKey={(r) => r.id}
        columns={columns}
        rowActions={(r) => (
          <span className="flex gap-2 justify-end">
            {r.paymentLink && r.status === "sent" && (
              <Button variant="ghost" size="sm" onClick={() => copyLink(r.paymentLink!)}>
                Copy link
              </Button>
            )}
            {r.status === "sent" && (
              <Button variant="link" size="sm" onClick={() => cancel(r.id)}>
                Cancel
              </Button>
            )}
          </span>
        )}
        empty={{ title: "No proposals yet" }}
      />

      {createOpen && (
        <CreateProposalModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void refetch();
          }}
        />
      )}
    </>
  );
}

function CreateProposalModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [orgId, setOrgId] = useState("");
  const [concept, setConcept] = useState("");
  const [amount, setAmount] = useState("");
  const [iva, setIva] = useState("21");
  const [days, setDays] = useState("14");
  const [busy, setBusy] = useState(false);
  const { run } = useApiCall();

  async function submit() {
    if (busy) return;
    const amountEur = Number(amount);
    if (!orgId.trim() || !concept.trim() || !Number.isFinite(amountEur) || amountEur <= 0) {
      return;
    }
    setBusy(true);
    const r = await run(
      () =>
        Proposals.create({
          orgId: orgId.trim(),
          concept: concept.trim(),
          amountEur,
          ivaRate: Number(iva) || 21,
          expiresInDays: Number(days) || 14,
        }),
      { successMessage: "Proposal created." },
    );
    setBusy(false);
    if (r) onCreated();
  }

  return (
    <Modal onClose={onClose} maxWidth="32rem">
      <h3 className="text-lg font-black mb-1 pr-6 text-ink">New proposal</h3>
      <p className="text-sm text-ink-3 mb-5">
        Stripe Checkout will be created with `invoice_creation` enabled, so the
        client gets an official invoice on payment.
      </p>
      <div className="space-y-3">
        <FormInput label="Org ID" value={orgId} onChange={setOrgId} placeholder="org_…" />
        <FormInput
          label="Concept"
          value={concept}
          onChange={setConcept}
          placeholder="Custom skill development for X feature"
        />
        <div className="grid grid-cols-3 gap-3">
          <FormInput label="Amount (€)" value={amount} onChange={setAmount} type="number" />
          <FormInput label="IVA %" value={iva} onChange={setIva} type="number" />
          <FormInput label="Expires (days)" value={days} onChange={setDays} type="number" />
        </div>
      </div>
      <div className="mt-6 flex gap-3">
        <Button variant="primary" onClick={submit} disabled={busy} fullWidth>
          {busy ? "Creating…" : "Create"}
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Modal>
  );
}
